import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePropertyAnalysis } from '../../hooks/usePropertyAnalysis';
import PaymentPage from '../PaymentPage';
import CreditsPage from '../CreditsPage';
import AccountPage from '../AccountPage';
import SettingsPage from '../SettingsPage';
import AdminDashboard from '../AdminDashboard';
import PropertyInput from '../PropertyInput';
import PropertyResults from '../PropertyResults';
import CompactURLBar from '../CompactURLBar';
import PropertyDetective from '../PropertyDetective';

/**
 * Protected Routes - Require authentication
 */

// Protected Route wrapper - redirects to login if not authenticated
export function ProtectedRoute({ children }) {
    const navigate = useNavigate();
    const { isAuthenticated, loading } = useAuth();

    useEffect(() => {
        if (!loading && !isAuthenticated) {
            navigate('/login', { replace: true });
        }
    }, [isAuthenticated, loading, navigate]);

    // Show loading state while checking auth
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    // Don't render protected content if not authenticated
    if (!isAuthenticated) {
        return null;
    }

    return children;
}

export function PaymentPageRoute() {
    const navigate = useNavigate();
    return (
        <PaymentPage
            onBack={() => navigate('/dashboard')}
        />
    );
}

export function CreditsPageRoute() {
    const navigate = useNavigate();
    return (
        <CreditsPage
            onBack={() => navigate('/dashboard')}
        />
    );
}

export function AccountPageRoute() {
    const navigate = useNavigate();
    return (
        <AccountPage
            onBack={() => navigate('/dashboard')}
        />
    );
}

export function SettingsPageRoute() {
    const navigate = useNavigate();
    return (
        <SettingsPage
            onBack={() => navigate('/dashboard')}
        />
    );
}

export function AdminDashboardRoute() {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Check if user is admin
    if (!user?.isAdmin) {
        navigate('/dashboard');
        return null;
    }

    return (
        <AdminDashboard
            onBack={() => navigate('/dashboard')}
        />
    );
}

export function ListingAnalysisRoute() {
    const navigate = useNavigate();
    const { analyze, loading, error, result, reset } = usePropertyAnalysis();
    const [showFullInput, setShowFullInput] = useState(true);
    const [analyzedURL, setAnalyzedURL] = useState('');

    // Collapse input when analysis completes
    useEffect(() => {
        if (result && !loading) {
            setShowFullInput(false);
            // Extract URL from result if available
            if (result.url) {
                setAnalyzedURL(result.url);
            }
        }
    }, [result, loading]);

    const handleEdit = () => {
        setShowFullInput(true);
    };

    const handleNewAnalysis = () => {
        reset();
        setShowFullInput(true);
        setAnalyzedURL('');
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="mb-6">
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <ArrowRight className="w-4 h-4 rotate-180" />
                        Back to Dashboard
                    </button>
                </div>

                {/* Show full input or compact bar based on state */}
                {showFullInput ? (
                    <PropertyInput
                        onAnalyze={(data) => {
                            // Store URL before analysis
                            if (data && data.url) {
                                setAnalyzedURL(data.url);
                            }
                            analyze(data);
                        }}
                        loading={loading}
                        error={error}
                        result={result}
                        reset={reset}
                    />
                ) : (
                    <CompactURLBar
                        url={analyzedURL}
                        onEdit={handleEdit}
                        onNewAnalysis={handleNewAnalysis}
                    />
                )}

                {result && <PropertyResults result={result} />}
            </div>
        </div>
    );
}

export function PhotoLocationSearchRoute() {
    const navigate = useNavigate();
    return (
        <div className="h-screen overflow-hidden relative">
            {/* Back button overlay */}
            <div className="absolute top-4 left-4 z-30">
                <button
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center gap-2 bg-white/90 backdrop-blur-sm text-gray-700 hover:text-gray-900 px-4 py-2 rounded-lg shadow-md transition-colors"
                >
                    <ArrowRight className="w-4 h-4 rotate-180" />
                    Back to Dashboard
                </button>
            </div>
            <PropertyDetective />
        </div>
    );
}
